// Displays information of a single Cafe in card format
import React, { useCallback, useEffect, useState, useRef, KeyboardEvent, MouseEvent } from "react";
import {
  Food24Regular,
  VehicleTruckProfile24Regular,
  Star24Regular,
  Star24Filled,
} from "@fluentui/react-icons";
import { Subtitle2, tokens } from "@fluentui/react-components";
import { ICafeListItemViewModel } from "@dining/api-client";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./CafeCardStyles";
import { appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { ConfirmationDialog, ConfirmationDialogProps } from "../../shared";
import {
  removeDefaultCafe,
  setDefaultCafe,
  addToFavoritesContent,
} from "../../../common/constants";

interface CafeCardProps {
  cafe: ICafeListItemViewModel;
}

export const CafeCard = ({ cafe }: CafeCardProps) => {
  const styles = useStyles();
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();
  const { cafeIdsSet: favoriteCafeIdsSet } = useAppStore((state) => state.favoriteCafes);
  const [appInsights, context, updateFavoriteCafe] = useAppStore((state) => [
    state.appInsights,
    state.context,
    state.favoriteCafes.updateFavoriteCafe,
  ]);
  const [favoritesDialog, setFavoritesDialog] = useState(false);
  const [closedDialog, setClosedDialog] = useState(false);
  // Ref for the Add/Remove to Favorites button
  const favoriteButtonRef = useRef<SVGSVGElement>(null);

  const getDialogBoxForAddToFavorites = useCallback(
    (
      titleStr: string,
      contentStr: string,
      confirmActionTrackEvent: string,
      dismissActionTrackEvent: string,
      updateOpt: boolean,
    ) => {
      return {
        content: contentStr,
        open: true,
        title: titleStr,
        confirmAction: () => {
          setFavoritesDialog(false);

          trackEvent(appInsights, confirmActionTrackEvent, context, {
            cafeId: cafe?.id,
            component: "CafeCard",
          });
          updateFavoriteCafe(cafe?.id, updateOpt);
          setDialogProps(undefined);
          setClosedDialog(true);
          // Use a timeout to ensure the dialog is fully closed before refocusing
          setTimeout(() => {
            favoriteButtonRef.current?.focus();
            setClosedDialog(false);
          }, 200);
        },
        dismissAction: () => {
          setFavoritesDialog(false);
          trackEvent(appInsights, dismissActionTrackEvent, context, {
            cafeId: cafe?.id,
            component: "CafeCard",
          });
          setDialogProps(undefined);
          setClosedDialog(true);
          // Restore focus to the Add/Remove to Favorites button
          setTimeout(() => {
            favoriteButtonRef.current?.focus();
            setClosedDialog(false);
          }, 200);
        },
      };
    },
    [context, appInsights, cafe, updateFavoriteCafe],
  );

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "CafeCard",
      cafeId: cafe.cafeId,
      cafeName: cafe.name,
    });
  }, [context, appInsights, cafe]);

  const navigate = useNavigate();
  const foodTruckRegex = new RegExp("food truck", "i"); // case insensitive

  const onClick = useCallback(() => {
    navigateOnKeyDownOrClick();
  }, [navigate, cafe, appInsights, context]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === "Space" || event.code === "Enter") {
      navigateOnKeyDownOrClick();
    }
  };

  const navigateOnKeyDownOrClick = () => {
    navigate("/cafe/" + cafe.id);
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.CafeSelected, context, {
      selectedCafeId: cafe.id,
      selectedCafeName: cafe.name,
    });
  };

  const handleKeyUpStarFilled = (event: KeyboardEvent) => {
    if (event.code === "Space" || event.code === "Enter") {
      removeFavoritesDialog(event);
    }
  };

  const handleKeyUpStarRegular = (event: KeyboardEvent) => {
    if (event.code === "Space" || event.code === "Enter") {
      addToFavoritesDialog(event);
    }
  };

  const addToFavoritesDialog = (event: MouseEvent | KeyboardEvent) => {
    if (closedDialog) return;
    setFavoritesDialog(true);
    event.stopPropagation();
    setDialogProps(
      getDialogBoxForAddToFavorites(
        setDefaultCafe,
        addToFavoritesContent,
        appInsightsEvents.Usage.Actions.AddToFavoriteCafesDialog.CafeAddConfirmed,
        appInsightsEvents.Usage.Actions.AddToFavoriteCafesDialog.CafeAddDismissed,
        true,
      ),
    );
  };

  const removeFavoritesDialog = (event: MouseEvent | KeyboardEvent) => {
    if (closedDialog) return;
    setFavoritesDialog(true);
    event.stopPropagation();
    setDialogProps(
      getDialogBoxForAddToFavorites(
        removeDefaultCafe,
        addToFavoritesContent,
        appInsightsEvents.Usage.Actions.AddToFavoriteCafesDialog.CafeRemoveConfirmed,
        appInsightsEvents.Usage.Actions.AddToFavoriteCafesDialog.CafeRemoveDismissed,
        false,
      ),
    );
  };

  return (
    <>
      <div>
        {dialogProps && (
          <ConfirmationDialog
            data-testid="dialog"
            {...dialogProps}
            favoritesDialog={favoritesDialog}
          />
        )}
      </div>
      <div className={styles.container} data-testid={cafe.id} onClick={onClick}>
        {foodTruckRegex.test(cafe.name) ? (
          <VehicleTruckProfile24Regular
            className={styles.icon}
            primaryFill={tokens.colorBrandForeground1}
          />
        ) : (
          <Food24Regular className={styles.icon} primaryFill={tokens.colorBrandForeground1} />
        )}
        <div
          className={styles.details}
          data-testid={cafe.id + "-cafeName"}
          aria-label={`(${cafe.name}) link`}
          aria-describedby={undefined} // Make sure there's no hidden description causing extra output
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <Subtitle2
            wrap={false}
            truncate
            className={styles.text}
            aria-hidden="true"
            data-testid="cafeName"
          >
            {cafe.name}
          </Subtitle2>
          <span aria-hidden="true">{cafe.serviceHours}</span>
        </div>

        {favoriteCafeIdsSet?.has(cafe.id) ? (
          <Star24Filled
            ref={favoriteButtonRef} // Attach ref to the filled star icon
            role="button" // Add role="button" for accessibility
            aria-label={"Remove " + cafe.name + " From Cafe Favorites"}
            className={styles.icon}
            primaryFill={tokens.colorBrandForeground1}
            data-testid={cafe.id + "-favRemove"}
            onKeyDown={(event) => {
              if (event.code === "Space") {
                event.preventDefault();
                return false;
              }
            }}
            onKeyUp={(event) => {
              handleKeyUpStarFilled(event);
            }}
            tabIndex={0}
            onClick={(event) => {
              removeFavoritesDialog(event);
            }}
          />
        ) : (
          <Star24Regular
            ref={favoriteButtonRef} // Attach ref to the regular star icon
            role="button" // Add role="button" for accessibility
            className={styles.icon}
            primaryFill={tokens.colorBrandForeground1}
            aria-label={"Add " + cafe.name + " To Cafe Favorites"}
            data-testid={cafe.id + "-favAdd"}
            onKeyDown={(event) => {
              if (event.code === "Space") {
                event.preventDefault();
                return false;
              }
            }}
            onKeyUp={(event) => {
              handleKeyUpStarRegular(event);
            }}
            tabIndex={0}
            onClick={(event) => {
              addToFavoritesDialog(event);
            }}
          />
        )}
      </div>
    </>
  );
};
